import { AuthScreen, Button } from '@command-center/ui-system';
import type { ReactNode } from 'react';
import { getBranding } from '../platform/branding';
import { useAuth } from './AuthProvider';
import { ResetPasswordForm } from './ResetPasswordForm';
import { SignInForm } from './SignInForm';

export function AuthGate({ children }: { children: ReactNode }) {
  const branding = getBranding();
  const {
    user,
    impersonation,
    isLoading,
    accessState,
    accessError,
    isPasswordRecovery,
    signOut,
  } = useAuth();

  if (isLoading) return <CenteredMessage title="Checking session…" />;

  if (!user && !impersonation) {
    return (
      <AuthScreen
        branding={branding}
        description="Secure access to Cascade leads, communications, Switchboard, and Voice operations."
      >
        <SignInForm />
      </AuthScreen>
    );
  }

  if (isPasswordRecovery) {
    return (
      <AuthScreen branding={branding} description="Securely update your Cascade account password.">
        <ResetPasswordForm />
      </AuthScreen>
    );
  }

  if (accessState === 'checking') return <CenteredMessage title="Verifying Cascade access…" />;

  if (accessState === 'denied') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <section className="max-w-md rounded-lg border border-border bg-card p-6 text-center shadow-sm">
          <h1 className="text-lg font-semibold text-foreground">Cascade access required</h1>
          <p className="mt-2 text-sm text-muted-foreground">{accessError}</p>
          <Button className="mt-4" variant="outline" onClick={() => void signOut()}>
            Sign out
          </Button>
        </section>
      </div>
    );
  }

  return children;
}

function CenteredMessage({ title }: { title: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background text-sm text-muted-foreground">
      {title}
    </div>
  );
}
